import { space } from '@atlaskit/editor-common/keymaps';
import type { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types/plugin-factory';
import { hasDocAsParent, isEmptyDocument, isEmptyParagraph } from '@atlaskit/editor-common/utils';
import { keymap } from '@atlaskit/editor-prosemirror/keymap';

import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../types/types';
import { createOpenAIModalCommand } from '../decoration/actions';
import { aiExperienceDecorationPluginKey } from '../decoration/decoration-plugin-key';

export function createSpaceShortcutPlugin(options: {
	aiGlobalOptIn: AIGlobalOptIn;
	editorPluginAIProvider: EditorPluginAIProvider;
	getIntl: PMPluginFactoryParams['getIntl'];
}): SafePlugin {
	return keymap({
		[`${space.common}`]: (state, dispatch, view) => {
			if (!view) {
				return false;
			}

			const { from, to, $to } = state.selection;
			if (
				options.editorPluginAIProvider.isInDocumentStreamingEnabled
					? from === to && isEmptyParagraph($to.parent) && hasDocAsParent($to)
					: isEmptyDocument(state.doc)
			) {
				const { editorPluginAIProvider, aiGlobalOptIn } = options;
				const configItem = editorPluginAIProvider.baseGenerate;

				const { ignoredSpaceTriggerSelection } =
					aiExperienceDecorationPluginKey.getState(state) ?? {};

				if (ignoredSpaceTriggerSelection) {
					try {
						if (state.selection.eq(ignoredSpaceTriggerSelection)) {
							// If there's an ignored selection set for the space trigger, then we want to avoid opening the AI modal
							// if it's trying to be opened at the same selection again. This is to stop situations where a users
							// opens by space and immediately presses Esc or space again. We want to allow them to use the editor as normal.
							return false;
						}
					} catch {
						// Default to opening the modal as usual.
					}
				}

				const openAIModalCommand = createOpenAIModalCommand({
					state,
					configItem,
					lastTriggeredFrom: 'spaceShortcut',
					triggerMethod: 'shortcut',
					aiGlobalOptIn: aiGlobalOptIn,
					locale: options.getIntl().locale,
				});

				// We must delay calling this command so that the experiences is mounted in a new stack, otherwise the keybindings
				// in the react modal will pickup this keypress and cause headaches for key tracking.
				setTimeout(() => openAIModalCommand(state, dispatch), 0);
				return true;
			}

			return false;
		},
	}) as SafePlugin;
}
